/**
 * Auto-select mixin for form selectors
 * Automatically selects the option when there's only one available option
 * 
 * Usage:
 * 1. Import and add to component mixins
 * 2. Define autoSelectConfig in component data/computed
 * 3. Call setupAutoSelect() in mounted/created hook
 * 
 * Example config:
 * {
 *   'field_name': {
 *     options: () => this.selectOptionsField,
 *     enabled: true, // Set to false to disable auto-select for this field
 *     skipIfValueExists: true // Don't auto-select if field already has a value
 *   }
 * }
 */
export const autoSelectMixin = {
  data() {
    return {
      // Configuration for auto-select behavior
      // Override in component to customize
      autoSelectConfig: {},
      // Track which fields have been auto-selected to prevent re-selection
      autoSelectedFields: new Set(),
    };
  },
  methods: {
    /**
     * Setup auto-select watchers based on configuration
     * Call this method in mounted/created hook
     */
    setupAutoSelect() {
      if (!this.autoSelectConfig || Object.keys(this.autoSelectConfig).length === 0) {
        return;
      }

      // Create watchers for each configured field
      Object.keys(this.autoSelectConfig).forEach((fieldName) => {
        const config = this.autoSelectConfig[fieldName];
        
        if (!config.enabled) {
          return;
        }

        // Watch the options array for this field
        this.$watch(
          () => {
            const optionsGetter = config.options;
            return typeof optionsGetter === 'function' ? optionsGetter() : optionsGetter;
          },
          (newOptions) => {
            this.handleAutoSelect(fieldName, newOptions, config);
          },
          { immediate: true, deep: true }
        );
      });
    },

    /**
     * Handle auto-selection logic for a specific field
     * @param {string} fieldName - Name of the field to auto-select
     * @param {Array} options - Array of options for the selector
     * @param {Object} config - Configuration object for this field
     */
    handleAutoSelect(fieldName, options, config) {
      // Skip if disabled
      if (!config.enabled) {
        return;
      }

      // Skip if already auto-selected (unless force is enabled)
      if (this.autoSelectedFields.has(fieldName) && !config.force) {
        return;
      }

      // Validate options array
      if (!Array.isArray(options) || options.length === 0) {
        return;
      }

      // Get current value from localResume or model
      const currentValue = this.getFieldValue(fieldName);

      // Skip if value already exists and skipIfValueExists is true
      if (config.skipIfValueExists && (currentValue !== null && currentValue !== undefined)) {
        return;
      }

      // Auto-select only if there's exactly one option
      if (options.length === 1) {
        const singleOption = options[0];
        // Extract value from option object (supports both 'value' and 'id' properties)
        let optionValue = null;
        if (singleOption.value !== undefined) {
          optionValue = singleOption.value;
        } else if (singleOption.id !== undefined) {
          optionValue = singleOption.id;
        }

        // Only auto-select if current value is null/undefined or different
        if (currentValue === null || currentValue === undefined || currentValue !== optionValue) {
          this.setFieldValue(fieldName, optionValue);
          this.autoSelectedFields.add(fieldName);
        }
      } else if (options.length > 1) {
        // If options change from 1 to multiple, remove from auto-selected set
        // This allows re-auto-selection if options change back to 1
        this.autoSelectedFields.delete(fieldName);
      }
    },

    /**
     * Get current value of a field
     * Override in component if field is nested differently
     * @param {string} fieldName - Name of the field
     * @returns {*} Current value of the field
     */
    getFieldValue(fieldName) {
      // Default implementation assumes localResume object
      if (this.localResume?.[fieldName] !== undefined) {
        return this.localResume[fieldName];
      }
      return null;
    },

    /**
     * Set value of a field
     * Override in component if field update mechanism is different
     * @param {string} fieldName - Name of the field
     * @param {*} value - Value to set
     */
    setFieldValue(fieldName, value) {
      // Default implementation assumes updateField method exists
      if (typeof this.updateField === 'function') {
        this.updateField(fieldName, value);
      } else if (this.localResume) {
        // Fallback: directly set on localResume using $set for Vue 2 reactivity
        this.$set(this.localResume, fieldName, value);
      }
    },

    /**
     * Reset auto-selected tracking for a field
     * Useful when manually changing a value
     * @param {string} fieldName - Name of the field to reset
     */
    resetAutoSelect(fieldName) {
      this.autoSelectedFields.delete(fieldName);
    },

    /**
     * Reset all auto-selected fields
     */
    resetAllAutoSelect() {
      this.autoSelectedFields.clear();
    },
  },
};
